// PATH: SaberpDeals/frontend/src/components/Shell.tsx
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { LogOut } from 'lucide-react';
import type { ReactNode } from 'react';

// Tabs. Only Catalog is live right now; the rest are placeholders for the
// slices we build next (Customers, People, New deal, Deals). They render a
// "coming soon" stub so the nav shows the full shape.
const tabs = [
    { to: '/catalog', label: 'Catalog' },
    { to: '/customers', label: 'Customers' },
    { to: '/people', label: 'People' },
    { to: '/new-deal', label: 'New deal' },
    { to: '/deals', label: 'Deals' },
];

export default function Shell({ children }: { children: ReactNode }) {
    const { username, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="max-w-[1140px] mx-auto px-6 pt-7 pb-24">
            <header className="flex items-center justify-between flex-wrap gap-3 pb-4 mb-2 border-b-[1.5px] border-ink">
                <div className="flex items-baseline gap-3">
                    <h1 className="font-disp text-[23px] font-semibold tracking-tight">SAB ERP</h1>
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-forest">
                        Deals &amp; Commissions
                    </span>
                </div>
                <nav className="flex gap-1 flex-wrap items-center">
                    {tabs.map((t) => (
                        <NavLink
                            key={t.to}
                            to={t.to}
                            className={({ isActive }) =>
                                `px-3.5 py-2 text-[13.5px] font-medium rounded-lg transition ${
                                    isActive ? 'text-forest bg-forest-soft' : 'text-ink-3 hover:text-ink-2 hover:bg-paper-2'
                                }`
                            }
                        >
                            {t.label}
                        </NavLink>
                    ))}
                    <button
                        onClick={() => { logout(); navigate('/login'); }}
                        title={`Sign out (${username ?? ''})`}
                        className="ml-2 p-2 text-ink-3 hover:text-clay rounded-lg transition"
                    >
                        <LogOut size={16} />
                    </button>
                </nav>
            </header>
            {children}
        </div>
    );
}

// Simple stub for tabs not yet built.
export function ComingSoon({ name }: { name: string }) {
    return (
        <div className="text-center py-24 text-ink-3">
            <div className="font-disp text-4xl mb-3 text-line-2">§</div>
            <div className="font-disp text-lg font-semibold text-ink-2 mb-1.5">{name}</div>
            <div className="text-sm">This screen is coming in a later build slice.</div>
        </div>
    );
}
